import { useMemo, useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Tabs } from 'antd';
import Content from 'layouts/Content';
import {
  ButtonExport,
  ButtonFilter,
  Button,
  InputForm,
  SelectForm,
} from 'components';
import DatePickerForm from 'components/DatePicker/DatePickerForm';
import { useTitle } from 'contexts/TitleProvider';
import Table from 'antd/es/table';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import DebtApis from 'apis/DebtApis';
import formatNumber from 'utils/constants/functions';
import dayjs from 'dayjs';
import { PlantApi } from 'apis/PlantApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { DefaultOptionType } from 'antd/es/select';
import LazySelectForm, {
  initLazySelectData,
  ILazySelectData,
  ILazySelectParams,
} from 'components/Select/LazySelectForm';

const DEFAULT_PAGE_SIZE = 10;

const sapColumns = [
  { title: 'STT', dataIndex: 'index', width: 60, align: 'center' },
  { title: 'Ngày tạo', dataIndex: 'createDate', width: 160 },
  {
    title: 'PNJP nợ',
    children: [
      {
        title: 'Q10',
        dataIndex: 'buyDebtQ10',
        align: 'right',
        width: 100,
        render: (value: any) => (value ? formatNumber(value) : '-'),
      },
      {
        title: 'VND',
        dataIndex: 'buyDebtVnd',
        align: 'right',
        width: 150,
        render: (value: any) => (value ? formatNumber(value) : '-'),
      },
    ],
  },
  {
    title: 'Khách hàng nợ',
    children: [
      {
        title: 'Q10',
        dataIndex: 'sellDebtQ10',
        align: 'right',
        width: 100,
        render: (value: any) => (value ? formatNumber(value) : '-'),
      },
      {
        title: 'VND',
        dataIndex: 'sellDebtVnd',
        align: 'right',
        width: 150,
        render: (value: any) => (value ? formatNumber(value) : '-'),
      },
    ],
  },
  {
    title: 'Đơn SO còn nợ',
    children: [
      {
        title: 'Q10',
        dataIndex: 'soQ10',
        align: 'right',
        width: 100,
        render: (value: any) => (value ? formatNumber(value) : '-'),
      },
      {
        title: 'VND',
        dataIndex: 'soVnd',
        align: 'right',
        width: 150,
        render: (value: any) => (value ? formatNumber(value) : '-'),
      },
    ],
  },
  {
    title: 'Đơn PO còn nợ',
    children: [
      {
        title: 'Q10',
        dataIndex: 'poQ10',
        align: 'right',
        width: 100,
        render: (value: any) => (value ? formatNumber(value) : '-'),
      },
      {
        title: 'VND',
        dataIndex: 'poVnd',
        align: 'right',
        width: 150,
        render: (value: any) => (value ? formatNumber(value) : '-'),
      },
    ],
  },
  {
    title: 'Bảng dẻ',
    dataIndex: 'orderIdPurchase',
    width: 140,
    align: 'center',
  },
  {
    title: 'Bảng kê bán hàng',
    dataIndex: 'orderIdSale',
    width: 140,
    align: 'center',
  },
  { title: 'Chi nhánh', dataIndex: 'branch', width: 100, align: 'center' },
  { title: 'Người tạo', dataIndex: 'creator', width: 100, align: 'center' },
];

const dmsColumns = [
  { title: 'STT', dataIndex: 'stt', width: 60, align: 'center' },
  { title: 'Ngày tạo', dataIndex: 'createDate', width: 160, align: 'center' },
  { title: 'Bảng THTT', dataIndex: 'sheetTHTT', width: 160, align: 'center' },
  {
    title: 'PNJP nợ',
    children: [
      { title: 'Q10', dataIndex: 'pnjpDebitQ10', align: 'right', width: 100 },
      {
        title: 'VND',
        dataIndex: 'pnjpDebitVnd',
        align: 'right',
        width: 150,
      },
    ],
  },
  {
    title: 'Khách hàng nợ',
    children: [
      {
        title: 'Q10',
        dataIndex: 'customerDebitQ10',
        align: 'right',
        width: 100,
      },
      {
        title: 'VND',
        dataIndex: 'customerDebitVnd',
        align: 'right',
        width: 150,
      },
    ],
  },
  { title: 'Ghi chú', dataIndex: 'note', width: 120, align: 'center' },
  {
    title: 'KH nợ: Hàng Truyền thống',
    children: [
      {
        title: 'Q10',
        dataIndex: 'traditionalGoodsQ10',
        align: 'right',
        width: 120,
      },
      {
        title: 'VND',
        dataIndex: 'traditionalGoodsVnd',
        align: 'right',
        width: 150,
      },
    ],
  },
  {
    title: 'KH nợ: Hàng Cao cấp',
    children: [
      {
        title: 'VND',
        dataIndex: 'premiumGoodsVnd',
        align: 'right',
        width: 150,
      },
    ],
  },
  {
    title: 'Hàng Cao cấp và Tiền công Hàng Truyền Thống',
    children: [
      {
        title: 'VND',
        dataIndex: 'premiumAndLaborVnd',
        align: 'right',
        width: 180,
      },
    ],
  },
  { title: 'Bảng dẻ', dataIndex: 'proposalSheet', width: 140, align: 'center' },
  {
    title: 'Bảng kê bán hàng',
    dataIndex: 'salesStatementSheet',
    width: 140,
    align: 'center',
  },
  { title: 'Tiền tệ', dataIndex: 'currency', width: 90, align: 'center' },
  { title: 'Đơn vị Q10', dataIndex: 'q10Unit', width: 100, align: 'center' },
  { title: 'Chi nhánh', dataIndex: 'branch', width: 100, align: 'center' },
  { title: 'Người tạo', dataIndex: 'creator', width: 150, align: 'center' },
  {
    title: 'Đội bán hàng',
    dataIndex: 'salesTeam',
    width: 120,
    align: 'center',
  },
];

export default function DebtCustomerDetailPage() {
  const { setTitle, setBreadcrumb } = useTitle();
  const location = useLocation();
  const [form] = Form.useForm();
  const [openFilter, setOpenFilter] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [sapData, setSapData] = useState<any[]>([]);
  const [dmsData, setDmsData] = useState<any[]>([]);
  const [pageInfo, setPageInfo] = useState({
    currentPage: 1,
    itemsPerPage: DEFAULT_PAGE_SIZE,
    totalItems: 0,
  });
  const [plantOptions, setPlantOptions] = useState<DefaultOptionType[]>([]);
  const [loadingPlant, setLoadingPlant] = useState(false);
  const [teamOptions, setTeamOptions] =
    useState<ILazySelectData>(initLazySelectData);
  const [loadingTeam, setLoadingTeam] = useState(false);

  const { mutate: fetchSapData, isPending: loadingSap } = useMutation({
    mutationFn: async ({
      sapCustomerId,
      plantId,
    }: {
      sapCustomerId: string;
      plantId?: string;
    }) => {
      console.log('🔍 fetchSapData mutationFn called with:', {
        sapCustomerId,
        plantId,
      });
      console.log(
        '🔍 fetchSapData plantId received:',
        plantId,
        'type:',
        typeof plantId,
      );
      const formValues = form.getFieldsValue();
      const input: any = {
        plantId: formValues.plantId || plantId || null,
        teamSaleId: formValues.teamId || null,
        sumPaymentId: null,
        orderIdSale: formValues.salesSheet || null,
        orderIdPurchase: formValues.purchaseSheet || null,
        effectiveTimeFrom: formValues.fromDate || null,
        effectiveTimeTo: formValues.toDate || null,
        sapCustomerId: sapCustomerId,
      };
      console.log(
        '🔍 fetchSapData input.plantId:',
        input.plantId,
        'type:',
        typeof input.plantId,
      );
      console.log('🔍 fetchSapData full input:', JSON.stringify(input));
      const skip = 0;
      const take = 100;
      const res = await DebtApis.getSapDebtClearingData(input, skip, take);
      return res?.data?.getSapDebtClearingData || {};
    },
    onSuccess: (result: any) => {
      const items = result?.items || [];
      const formattedData = items.map((item: any, index: number) => ({
        ...item,
        index: index + 1,
        createDate: item.createDate
          ? dayjs(item.createDate).format('DD/MM/YYYY HH:mm:ss')
          : '-',
        buyDebtQ10: item.buyDebtQ10 || 0,
        buyDebtVnd: item.buyDebtVnd || 0,
        sellDebtQ10: item.sellDebtQ10 || 0,
        sellDebtVnd: item.sellDebtVnd || 0,
        soQ10: item.soQ10 || 0,
        soVnd: item.soVnd || 0,
        poQ10: item.poQ10 || 0,
        poVnd: item.poVnd || 0,
        orderIdPurchase: item.orderIdPurchase || '-',
        orderIdSale: item.orderIdSale || '-',
        branch: item.tplant?.name || item.storeId || '-',
        creator: item.tcreateUser?.fullName || 'SAP',
      }));
      setSapData(formattedData);
      setPageInfo(
        result?.pageInfo || {
          currentPage: 1,
          itemsPerPage: DEFAULT_PAGE_SIZE,
          totalItems: 0,
        },
      );
    },
    onError: (error: any) => {
      console.error('Error fetching SAP data:', error);
      setSapData([]);
    },
  });

  const { mutate: fetchDmsData, isPending: loadingDms } = useMutation({
    mutationFn: async ({
      customerId,
      plantId,
    }: {
      customerId: string;
      plantId?: string;
    }) => {
      console.log('🔍 fetchDmsData mutationFn called with:', {
        customerId,
        plantId,
      });
      console.log(
        '🔍 fetchDmsData plantId received:',
        plantId,
        'type:',
        typeof plantId,
      );
      const formValues = form.getFieldsValue();
      const input: any = {
        plantId: formValues.plantId || plantId || null,
        teamSaleId: formValues.teamId || null,
        customerId: customerId,
        effectiveTimeFrom: formValues.fromDate || null,
        effectiveTimeTo: formValues.toDate || null,
        skip: 0,
        take: 100,
      };
      console.log(
        '🔍 fetchDmsData input.plantId:',
        input.plantId,
        'type:',
        typeof input.plantId,
      );
      console.log('🔍 fetchDmsData full input:', JSON.stringify(input));
      const res = await DebtApis.getCustomerDebitComparisonTHTT(input);
      return res?.data?.getCustomerDebitComparisonTHTT || {};
    },
    onSuccess: (result: any) => {
      const items = result?.items || [];

      const mapped = items.map((item: any, idx: number) => ({
        stt: idx + 1,
        id: item.sumPaymentId,
        createDate: item.createDate
          ? dayjs(item.createDate).format('DD/MM/YYYY HH:mm:ss')
          : '-',
        sheetTHTT: item.sumPaymentId || '-',
        // PNJP nợ
        pnjpDebitQ10: formatNumber(item.dmsPayDebitQ10 || 0),
        pnjpDebitVnd: formatNumber(item.dmsPayDebitAmount || 0),
        // Khách hàng nợ
        customerDebitQ10: formatNumber(item.dmsCollectDebitQ10 || 0),
        customerDebitVnd: formatNumber(item.dmsCollectDebitAmount || 0),
        note: '-',
        // KH nợ: Hàng Truyền thống
        traditionalGoodsQ10: formatNumber(item.traditionCustomerDebitQ10 || 0),
        traditionalGoodsVnd: formatNumber(
          item.traditionCustomerDebitAmount || 0,
        ),
        // KH nợ: Hàng Cao cấp
        premiumGoodsVnd: formatNumber(item.highendCustomerDebitAmount || 0),
        // Hàng Cao cấp và Tiền công Hàng Truyền Thống
        premiumAndLaborVnd: formatNumber(
          item.amountTraditionalHighendLimit || 0,
        ),
        proposalSheet: item.purchaseId || '-',
        salesStatementSheet: item.orderId || '-',
        currency: item.unitPrice || 'VND',
        q10Unit: item.unitQ10 || 'Chi',
        branch: item.plantId || '-',
        creator: 'Hệ thống',
        salesTeam: '-',
      }));
      setDmsData(mapped);
      setPageInfo({
        currentPage: result?.pageInfo?.currentPage || 1,
        itemsPerPage: result?.pageInfo?.itemsPerPage || DEFAULT_PAGE_SIZE,
        totalItems: result?.pageInfo?.totalItems || mapped.length,
      });
    },
  });

  useMemo(() => {
    setBreadcrumb([
      { title: 'Kế toán/Thủ quỹ' },
      { title: 'Công nợ' },
      { title: 'Chi tiết công nợ' },
    ]);
  }, []);

  // Gọi API khi vào trang
  useEffect(() => {
    console.log('🔍 DebtCustomerDetailPage location.state:', location.state);
    console.log('🔍 DebtCustomerDetailPage location:', location);
    console.log('🔍 DebtCustomerDetailPage search:', location.search);

    // Try to get customerId, sapCustomerId, customerName, and plantId from URL params first, then from state
    const urlParams = new URLSearchParams(location.search);
    const customerIdFromUrl = urlParams.get('customerId');
    const sapCustomerIdFromUrl = urlParams.get('sapCustomerId');
    const customerNameFromUrl = urlParams.get('customerName');
    const plantIdFromUrl = urlParams.get('plantId');
    const customerIdFromState = location.state?.customerId;
    const sapCustomerIdFromState = location.state?.sapCustomerId;
    const customerNameFromState = location.state?.customerName;
    const plantIdFromState = location.state?.plantId;

    const customerId = customerIdFromUrl || customerIdFromState; // DMS customer ID
    const sapCustomerId =
      sapCustomerIdFromUrl || sapCustomerIdFromState || customerId; // SAP customer ID (fallback to customerId)
    const customerName = decodeURIComponent(
      customerNameFromUrl || customerNameFromState || '',
    );
    const plantId =
      plantIdFromUrl && plantIdFromUrl !== 'null'
        ? plantIdFromUrl
        : plantIdFromState || null;

    console.log('🔍 DebtCustomerDetailPage customerId:', customerId);
    console.log('🔍 DebtCustomerDetailPage sapCustomerId:', sapCustomerId);
    console.log('🔍 DebtCustomerDetailPage customerName:', customerName);
    console.log('🔍 DebtCustomerDetailPage plantId1:', plantId);
    console.log('🔍 DebtCustomerDetailPage plantIdFromUrl:', plantIdFromUrl);
    console.log(
      '🔍 DebtCustomerDetailPage plantIdFromState:',
      plantIdFromState,
    );
    console.log('🔍 DebtCustomerDetailPage plantId type:', typeof plantId);

    // Update title with customer name
    if (customerId && customerName) {
      setTitle(`Công nợ: ${customerId} - ${customerName}`);
    } else if (customerId) {
      setTitle(`Công nợ: ${customerId}`);
    }

    console.log('🔍 About to call fetchDmsData with:', { customerId, plantId });
    fetchDmsData({ customerId: customerId, plantId });
    console.log('🔍 About to call fetchSapData with:', {
      sapCustomerId,
      plantId,
    });
    fetchSapData({ sapCustomerId: sapCustomerId, plantId });
  }, [location.state, location.search, fetchDmsData, fetchSapData, setTitle]);

  // Load danh sách chi nhánh
  useEffect(() => {
    setLoadingPlant(true);
    PlantApi.getAllPlantFilterHO()
      .then((res: any) => {
        const items = res?.data?.getPlant?.items || [];
        const filtered = items.filter((item: any) => item.id !== '5000');
        setPlantOptions(
          filtered.map((item: any) => ({
            label: item.id + (item.name ? ` - ${item.name}` : ''),
            value: item.id,
          })),
        );
      })
      .finally(() => setLoadingPlant(false));
  }, []);

  const handleChangePlant = useCallback(
    (plantId: string) => {
      form.setFieldValue('teamId', undefined);
      setTeamOptions(initLazySelectData);

      if (plantId) {
        setLoadingTeam(true);
        SaleTeamApi.getAllTeamSaleCb({
          pageSize: 50,
          value: '',
          plantId: plantId,
        })
          .then((res: any) => {
            const data = res?.data?.getTeamSales;
            setTeamOptions({
              options:
                data?.items?.map((item: any) => ({
                  label: `${item?.subId} - ${item?.name}`,
                  value: item?.id,
                })) || [],
              totalItems: data?.pageInfo?.totalItems || 0,
            });
          })
          .finally(() => setLoadingTeam(false));
      }
    },
    [form],
  );

  const handleTeamSelectScrollChange = useCallback(
    (params: ILazySelectParams) => {
      const plantId = form.getFieldValue('plantId');
      setLoadingTeam(true);
      SaleTeamApi.getAllTeamSaleCb({
        ...params,
        plantId: plantId,
      } as any)
        .then((res: any) => {
          const data = res?.data?.getTeamSales;
          setTeamOptions({
            options:
              data?.items?.map((item: any) => ({
                label: `${item?.subId} - ${item?.name}`,
                value: item?.id,
              })) || [],
            totalItems: data?.pageInfo?.totalItems || 0,
          });
        })
        .finally(() => setLoadingTeam(false));
    },
    [form],
  );

  const handleExport = async () => {
    setExportLoading(true);
    try {
      // TODO: export with exportExcel_v2 when API/data wired
    } finally {
      setExportLoading(false);
    }
  };

  const handleSearch = (values: any) => {
    setPageInfo((prev) => ({ ...prev, currentPage: 1 }));

    // Get customer ID from URL params
    const urlParams = new URLSearchParams(location.search);
    const customerId = urlParams.get('customerId') || undefined;
    const sapCustomerId = urlParams.get('sapCustomerId') || customerId;
    const plantId = urlParams.get('plantId') || undefined;

    // Refetch data with filters
    if (customerId) {
      fetchDmsData({ customerId, plantId });
    }
    if (sapCustomerId) {
      fetchSapData({ sapCustomerId, plantId });
    }
  };

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={openFilter} setIsOpen={setOpenFilter} />
        </Col>
        <Col>
          <ButtonExport loading={exportLoading} onClick={handleExport} />
        </Col>
      </Row>
      {openFilter && (
        <Content>
          <Form form={form} layout="vertical" onFinish={handleSearch}>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item label="Chi nhánh" name="plantId">
                  <SelectForm
                    placeholder="Chọn chi nhánh"
                    options={plantOptions}
                    loading={loadingPlant}
                    onChange={handleChangePlant}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Đội" name="teamId">
                  <LazySelectForm
                    placeholder="Chọn đội"
                    data={teamOptions}
                    loading={loadingTeam}
                    onSelectScrollChange={handleTeamSelectScrollChange}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Bảng kê bán hàng" name="salesSheet">
                  <InputForm placeholder="Tìm kiếm Bảng kê bán hàng" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Bảng dẻ" name="purchaseSheet">
                  <InputForm placeholder="Tìm kiếm Bảng dẻ" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item label="Từ ngày" name="fromDate">
                  <DatePickerForm className="w-full" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Đến ngày" name="toDate">
                  <DatePickerForm className="w-full" />
                </Form.Item>
              </Col>
              <Col span={12}>{/* Cột trống để căn chỉnh */}</Col>
            </Row>
            <Row gutter={[16, 16]} justify="end">
              <Col span={18}>{/* Cột trống để căn chỉnh */}</Col>
              <Col span={6}>
                <div className="flex gap-2 justify-end">
                  <Button
                    title="Hủy"
                    type="button"
                    handleOnclick={() => {
                      form.resetFields();
                      // Reset team options
                      setTeamOptions(initLazySelectData);
                      // Reload data with initial state
                      const urlParams = new URLSearchParams(location.search);
                      const customerId =
                        urlParams.get('customerId') || undefined;
                      const sapCustomerId =
                        urlParams.get('sapCustomerId') || customerId;
                      const plantId = urlParams.get('plantId') || undefined;

                      if (customerId) {
                        fetchDmsData({ customerId, plantId });
                      }
                      if (sapCustomerId) {
                        fetchSapData({ sapCustomerId, plantId });
                      }
                    }}
                  />
                  <Button fill title="Tìm kiếm" type="submit" />
                </div>
              </Col>
            </Row>
          </Form>
        </Content>
      )}
      <Content>
        <Tabs defaultActiveKey="dms">
          <Tabs.TabPane tab="Công nợ SAP" key="sap">
            <Table
              columns={sapColumns as any}
              dataSource={sapData}
              rowKey={(r: any) => r?.id}
              loading={loadingSap}
              pagination={{
                showSizeChanger: true,
                className: '!mb-0 !mt-5',
                pageSizeOptions: ['10', '20', '50'],
                current: pageInfo.currentPage,
                pageSize: pageInfo.itemsPerPage,
                total: pageInfo.totalItems,
              }}
              scroll={{ x: 'max-content' }}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Công nợ DMS" key="dms">
            <Table
              columns={dmsColumns as any}
              dataSource={dmsData}
              rowKey={(r: any) => r?.id}
              loading={loadingDms}
              pagination={{
                showSizeChanger: true,
                className: '!mb-0 !mt-5',
                pageSizeOptions: ['10', '20', '50'],
                current: pageInfo.currentPage,
                pageSize: pageInfo.itemsPerPage,
                total: pageInfo.totalItems,
              }}
              scroll={{ x: 'max-content' }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Content>
    </>
  );
}
